import SearchAndFilterBar from "components/general/SearchAndFilterBar";
import CustomSelect from "components/general/CustomSelect";
import { Col, Container, Row, InputGroup } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { OrdersTable } from "./ordersTable/OrdersTable";
import CustomPagination from "components/general/CustomPagination";
import { useAuthStore } from "../../zustand/loginStore";
import { createAuthenticatedAxiosInstance, fetchOrders } from "api/auth";
import { useEffect, useState } from "react";
import { ISelectableOption } from "interfaces/ISelectableOption";
import { isSuccessStatusCode } from "helpers/httpResponse";
import { IOrder } from "interfaces/IOrder";
import { IClickedHeader } from "interfaces/ITable";
import { RequestStatesTypes } from "types/RequestsStatesTypes";
import { getPaginatedSlice } from "helpers/pagination";
import { removeAccents } from "helpers/texts";
import { sortByKey } from "helpers/sorting";

/**
 * The orders main component
 */
export const Orders = () => {
    const { token } = useAuthStore();
    const axiosInstance = createAuthenticatedAxiosInstance(token);
    const [orders, setOrders] = useState<IOrder[]>([]);
    const [filteredOrders, setFilteredOrders] = useState<IOrder[]>(orders);
    const [paginatedData, setPaginatedData] = useState<IOrder[]>([]);
    const [clickedHeader, setClickedHeader] = useState<IClickedHeader>({ name: "", asc: false });
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [requestState, setRequestState] = useState<RequestStatesTypes>("none");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [noResults, setNoResults] = useState<boolean>(false);
    const [apiError, setApiError] = useState<boolean>(false);
    const [pageSize, setPageSize] = useState<number>(5);
    const filterOptions: ISelectableOption[] = [
        { value: "Correo", label: "Correo" },
        { value: "Estado", label: "Estado" },
        { value: "Numero", label: "No. de Pedido" },
        { value: "Todos", label: "Todos" }
    ];
    const [filter, setFilter] = useState<ISelectableOption | null>(null);

    /**
     * Per page options for pagination
     * @constant {Array<{value: string, label: string}>}
     */
    const perPageOptions = [
        { value: "5", label: "5 por página" },
        { value: "10", label: "10 por página" },
        { value: "25", label: "25 por página" },
        { value: "50", label: "50 por página" },
        { value: "100", label: "100 por página" },
        { value: "200", label: "200 por página" },
    ];

    /**
     * Per page filter state for CustomSelect
     */
    const [perPageSelect, setPerPageSelect] = useState<{ value: string; label: string } | null>({ value: "5", label: "5 por página" });

    /**
     * handles the header clicked and orders the information accordingly
     * @param key is the object key represented by the header (may not be equal)
     * @param asc true if ascending order, descending otherwise
     */
    const handleOrderBy = async (key: string, asc: boolean) => {
        const typedKey = key as keyof IOrder;
        const orderedOrders = sortByKey(filteredOrders, typedKey, asc);
        setFilteredOrders(orderedOrders);
        setClickedHeader({ name: key, asc });
    }

    /**
     * handles the filter change and filters the data and returns to the first page
     * @param option is the filter option selected
     */
    const handleFilterChange = (option: ISelectableOption | null) => {
        setFilter(option);
    }

    /**
     * Handles per page filter change
     * @function handlePerPageChange
     * @param {{ value: string; label: string } | null} newValue - The new per page value
     */
    const handlePerPageChange = (newValue: { value: string; label: string } | null) => {
        setPerPageSelect(newValue);
        setPageSize(parseInt(newValue?.value || "5"));
        setCurrentPage(1);
    };

    /**
    * handles the selected filter and filters the current data
    * @param option is the selected filter option
    */
    const handleFilter = (input: string): IOrder[] => {
        let search: IOrder[] = [];
        setClickedHeader({ name: "", asc: false });

        if (!filter) {
            return search = orders.filter(order =>
                order.tracking_number.toString().toLowerCase().includes(input) ||
                order.user_email.toLowerCase().toLowerCase().includes(input) ||
                new Date(order.created_at).toLocaleString().includes(input) ||
                order.total.toString().includes(input) ||
                order.status.toLowerCase().includes(input)
            );
        } else if (filter.value === "Todos") {
            return search = orders.filter(order =>
                order.tracking_number.toString().toLowerCase().includes(input) ||
                order.user_email.toLowerCase().toLowerCase().includes(input) ||
                new Date(order.created_at).toLocaleString().includes(input) ||
                order.total.toString().includes(input) ||
                removeAccents(order.status.toLowerCase()).includes(removeAccents(input))
            );
        }

        const { value } = filter;

        switch (value) {
            case "Correo":
                return search = orders.filter(order => order.user_email.includes(input));
            case "Estado":
                return search = orders.filter(order => removeAccents(order.status.toLowerCase()).includes(removeAccents(input)));
            case "Numero":
                return search = orders.filter(order => order.tracking_number.toString().includes(input));
            default:
                return orders;
        }
    }

    /**
     * handles the search term and returns to the first page
     * @param value is the input provided
    */
    const handleSearch = (value: string) => {
        setClickedHeader({ name: "", asc: false });
        setSearchTerm(value);
        const trimmedValue = value.toLowerCase().trim();
        if (trimmedValue === "") {
            setFilteredOrders(orders);
            setNoResults(false);
            return;
        }

        const searchedOrders = handleFilter(trimmedValue);

        setNoResults(searchedOrders.length === 0);
        setFilteredOrders(searchedOrders);
        setCurrentPage(1);
    }

    /**
    * Method that fetch the data of orders
    */
    const fetchData = async () => {
        setRequestState("loading");
        const { data, statusCode } = await fetchOrders(axiosInstance, {
            per_page: 1000,
            page: 1,
        });
        if (!isSuccessStatusCode(statusCode)) {
            setApiError(true);
            setRequestState("failure");
            return;
        }
        if (!data)
            return;
        setRequestState("successful");
        const { orders } = data;
        setApiError(false);
        setOrders(orders);
        setFilteredOrders(orders);
    }

    /**
     * handles the page change from a direct click or the other navigation buttons
     * @param pageNumber the page number clicked
     */
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    }

    /**
     * This use effect process the orders data and paginates it
     * it also recalculate the total pages by listening the actual filtered data
     * [filteredOrders] is a filtered collection of orders data
     * [currentPage] is the actual page clicked from pagination component
     * [pageSize] is the current page size
     */
    useEffect(() => {
        const data = getPaginatedSlice(filteredOrders, currentPage, pageSize);
        const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
        setTotalPages(totalPages);
        setPaginatedData(data);
    }, [filteredOrders, currentPage, pageSize, clickedHeader]);

    /**
     * use effect that fetch the initial data
     */
    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            <Container className="px-4 px-md-5 d-flex flex-column">
                <Row className="align-items-center mb-4 mt-5">
                    <Col className="text-center mt-5">
                        <h2 className="fw-bold">Pedidos realizados</h2>
                    </Col>
                </Row>
                <SearchAndFilterBar
                    searchType={"text"}
                    searchTerm={searchTerm}
                    onSearchChange={handleSearch}
                    filterOptions={filterOptions}
                    filterBy={filter}
                    isSearchDisabled={false}
                    onFilterChange={handleFilterChange}
                    customTooltip="Ordena las columnas por No. de pedido, fecha o estado"
                />

                {/* Per page selector - aligned below the filter */}
                <Row className="mb-3">
                    <Col xs={6}></Col>
                    <Col xs={6} className="d-flex justify-content-end">
                        <div className="d-flex align-items-center w-75">
                            <InputGroup style={{ height: "38px", width: "100%" }}>
                                <div className="filter-select-container" style={{ 
                                    flex: "1", 
                                    height: "100%",
                                    minWidth: "0",
                                    width: "100%"
                                }}>
                                    <CustomSelect
                                        options={perPageOptions}
                                        value={perPageSelect}
                                        onChange={handlePerPageChange}
                                        placeholder="Elementos por página..."
                                        isFilteredIconVisible={false}
                                    />
                                </div>
                            </InputGroup>
                        </div>
                    </Col>
                </Row>
                <OrdersTable
                    clickedHeader={clickedHeader}
                    orderBy={handleOrderBy}
                    requestState={requestState}
                    paginatedData={paginatedData}
                    noResults={noResults}
                    apiError={apiError}
                />
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    totalResults={filteredOrders.length ?? 0}
                />
            </Container>
        </>
    )
}
